import { Controller } from "stimulus";

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6,8}$/;
const HEX_RGB_REGEX = /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})/;
const WCAG_AAA_MIN_CONTRAST = 7.0;

function hexToRgb(hex) {
  if (!hex) {
    return null;
  }
  const match = hex.match(HEX_RGB_REGEX);
  if (!match) {
    return null;
  }

  return [
    parseInt(match[1], 16) / 255.0,
    parseInt(match[2], 16) / 255.0,
    parseInt(match[3], 16) / 255.0,
  ];
}

function hexToRgb255(hex) {
  if (!hex) {
    return null;
  }
  const match = hex.match(HEX_RGB_REGEX);
  if (!match) {
    return null;
  }

  return [
    parseInt(match[1], 16),
    parseInt(match[2], 16),
    parseInt(match[3], 16),
  ];
}

function rgbToHex(r, g, b) {
  const toHex = (c) => {
    const hex = Math.round(Math.min(255, Math.max(0, c))).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return 0;
  }

  const [r, g, b] = rgb.map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function calculateContrastRatio(color1, color2) {
  const l1 = relativeLuminance(color1);
  const l2 = relativeLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

function adjustBackgroundForContrast(bgHex, textHex, targetRatio = 7.0) {
  const textLum = relativeLuminance(textHex);
  const rgb = hexToRgb255(bgHex);
  if (!rgb) {
    return null;
  }

  if (textLum >= 0.5) {
    let factor = 0.95;
    for (let i = 0; i < 50; i += 1) {
      const adjusted = rgbToHex(rgb[0] * factor, rgb[1] * factor, rgb[2] * factor);
      if (calculateContrastRatio(adjusted, textHex) >= targetRatio) {
        return adjusted;
      }
      factor *= 0.90;
    }
    return "#000000";
  }

  let factor = 0.1;
  for (let i = 0; i < 50; i += 1) {
    const r = rgb[0] + (255 - rgb[0]) * factor;
    const g = rgb[1] + (255 - rgb[1]) * factor;
    const b = rgb[2] + (255 - rgb[2]) * factor;
    const adjusted = rgbToHex(r, g, b);
    if (calculateContrastRatio(adjusted, textHex) >= targetRatio) {
      return adjusted;
    }
    factor += 0.05;
  }
  return "#FFFFFF";
}

function isValidHexColor(color) {
  return typeof color === "string" && HEX_COLOR_REGEX.test(color.trim());
}

export default class extends Controller {
  static targets = [
    "nameInput",
    "bgInput",
    "textInput",
    "previewTag",
    "contrastBox",
    "ratioValue",
    "statusBadge",
    "suggestionsContainer",
    "suggestionsList",
  ];

  connect() {
    this.update();
  }

  update() {
    const name = this.getName();
    const bgColor = this.getBgColor();
    const textColor = this.getTextColor();

    this.updatePreview(name, bgColor, textColor);
    this.updateContrast(bgColor, textColor);
  }

  getName() {
    if (this.hasNameInputTarget && this.nameInputTarget.value) {
      return this.nameInputTarget.value.trim();
    }
    const input = this.element.querySelector('input[name*="[name]"]');
    return input ? input.value.trim() : "";
  }

  getBgColor() {
    if (this.hasBgInputTarget && this.bgInputTarget.value) {
      return this.bgInputTarget.value.trim();
    }
    const input = this.bgInputElement;
    return input ? input.value.trim() : "";
  }

  getTextColor() {
    if (this.hasTextInputTarget && this.textInputTarget.value) {
      return this.textInputTarget.value.trim();
    }
    const input = this.textInputElement;
    return input ? input.value.trim() : "";
  }

  get bgInputElement() {
    const selector = 'input[name*="[background_color]"], ' +
      'input[name*="[navbar_background_color]"], ' +
      'input[name*="[primary_color]"]';
    return this.element.querySelector(selector);
  }

  get textInputElement() {
    const selector = 'input[name*="[text_color]"], ' +
      'input[name*="[navbar_text_color]"], ' +
      'input[name*="[secondary_color]"]';
    return this.element.querySelector(selector);
  }

  get bgInput() {
    return this.hasBgInputTarget ? this.bgInputTarget : this.bgInputElement;
  }

  get textInput() {
    return this.hasTextInputTarget ? this.textInputTarget : this.textInputElement;
  }

  updatePreview(name, bgColor, textColor) {
    if (!this.hasPreviewTagTarget) {
      return;
    }

    this.previewTagTarget.textContent = name || "Tag Preview";

    if (isValidHexColor(bgColor)) {
      this.previewTagTarget.style.backgroundColor = bgColor;
    }

    if (isValidHexColor(textColor)) {
      this.previewTagTarget.style.color = textColor;
    }
  }

  updateContrast(bgColor, textColor) {
    const hasValidColors = isValidHexColor(bgColor) && isValidHexColor(textColor);

    if (!hasValidColors) {
      if (this.hasRatioValueTarget) {
        this.ratioValueTarget.textContent = "--:1";
      }
      if (this.hasStatusBadgeTarget) {
        this.statusBadgeTarget.textContent = "Fail";
        this.statusBadgeTarget.className = "tag is-danger";
      }
      if (this.hasContrastBoxTarget) {
        this.contrastBoxTarget.style.borderColor = "#f14668";
      }
      this.renderSuggestions([]);
      return;
    }

    const ratio = calculateContrastRatio(bgColor, textColor);
    const formattedRatio = `${ratio.toFixed(2)}:1`;
    const passesAAA = ratio >= WCAG_AAA_MIN_CONTRAST;

    if (this.hasRatioValueTarget) {
      this.ratioValueTarget.textContent = formattedRatio;
    }

    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = passesAAA ? "Pass" : "Fail";
      this.statusBadgeTarget.className = passesAAA ? "tag is-success" : "tag is-danger";
    }

    if (this.hasContrastBoxTarget) {
      this.contrastBoxTarget.style.borderColor = passesAAA ? "#48c78e" : "#f14668";
    }

    if (passesAAA) {
      this.renderSuggestions([]);
    } else {
      const suggestions = this.generateSuggestions(bgColor, textColor);
      this.renderSuggestions(suggestions);
    }
  }

  generateSuggestions(bgColor, textColor) {
    const suggestions = [];
    const ratioWhite = calculateContrastRatio(bgColor, "#FFFFFF");
    const ratioBlack = calculateContrastRatio(bgColor, "#000000");

    if (ratioWhite >= WCAG_AAA_MIN_CONTRAST) {
      suggestions.push({
        label: `Set text to White (${ratioWhite.toFixed(2)}:1)`,
        type: "text",
        value: "#FFFFFF",
      });
    }

    if (ratioBlack >= WCAG_AAA_MIN_CONTRAST) {
      suggestions.push({
        label: `Set text to Black (${ratioBlack.toFixed(2)}:1)`,
        type: "text",
        value: "#000000",
      });
    }

    const adjustedBg = adjustBackgroundForContrast(bgColor, textColor, WCAG_AAA_MIN_CONTRAST);
    if (adjustedBg && adjustedBg.toUpperCase() !== bgColor.toUpperCase()) {
      const adjustedRatio = calculateContrastRatio(adjustedBg, textColor);
      const isDarker = relativeLuminance(adjustedBg) < relativeLuminance(bgColor);
      suggestions.push({
        label: `${isDarker ? "Darken" : "Lighten"} background (${adjustedRatio.toFixed(2)}:1)`,
        type: "bg",
        value: adjustedBg,
      });
    }

    return suggestions;
  }

  renderSuggestions(suggestions) {
    if (!this.hasSuggestionsContainerTarget) {
      return;
    }

    if (suggestions.length === 0) {
      this.suggestionsContainerTarget.style.display = "none";
      return;
    }

    this.suggestionsContainerTarget.style.display = "";

    if (this.hasSuggestionsListTarget) {
      this.suggestionsListTarget.innerHTML = "";
      suggestions.forEach((sugg) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "button is-small is-info is-light mr-2 mb-1";
        btn.textContent = sugg.label;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.applySuggestion(sugg.type, sugg.value);
        });
        this.suggestionsListTarget.appendChild(btn);
      });
    }
  }

  autoFix(event) {
    if (event) {
      event.preventDefault();
    }
    const bgColor = this.getBgColor();
    const textColor = this.getTextColor();
    const suggestions = this.generateSuggestions(bgColor, textColor);

    if (suggestions.length > 0) {
      const best = suggestions[0];
      this.applySuggestion(best.type, best.value);
    }
  }

  applySuggestion(type, value) {
    const input = type === "text" ? this.textInput : this.bgInput;
    if (input) {
      input.value = value;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.dispatchEvent(new Event("keyup", { bubbles: true }));
    }
    this.update();
  }
}
